import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AppointmentBooking from "@/components/AppointmentBooking";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import WhatsAppButton from "@/components/WhatsAppButton";

// Declare global grecaptcha
declare global {
  interface Window {
    grecaptcha: any;
  }
}
const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const scrollToElementWithOffset = (elementId: string, callback?: () => void) => {
    const element = document.getElementById(elementId);
    if (element) {
      const headerHeight = 100; // Account for sticky header + padding
      const elementPosition = element.offsetTop - headerHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: "smooth",
      });
      if (callback) {
        setTimeout(callback, 800);
      }
    }
  };
  useEffect(() => {
    const shouldGoAfspraak = window.location.hash === "#afspraak" || sessionStorage.getItem("goAfspraak") === "1";
    const shouldGoBericht = window.location.hash === "#bericht";
    if (shouldGoAfspraak) {
      try {
        sessionStorage.removeItem("goAfspraak");
      } catch {}
      setTimeout(() => {
        scrollToElementWithOffset("afspraak", () => {
          const el = document.getElementById("afspraak");
          const link = el?.querySelector('a[href="#"]') as HTMLAnchorElement | null;
          link?.click();
        });
      }, 100);
    }
    if (shouldGoBericht) {
      setTimeout(() => {
        scrollToElementWithOffset("bericht");
      }, 100);
    }
  }, []);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Execute reCAPTCHA with proper ready check
      let recaptchaToken = "";
      console.log("Form submitted, checking reCAPTCHA...");
      if (window.grecaptcha) {
        console.log("grecaptcha found, checking ready...");
        await new Promise<void>((resolve) => {
          window.grecaptcha.ready(() => {
            console.log("reCAPTCHA is ready");
            resolve();
          });
        });
        try {
          console.log("Executing reCAPTCHA...");
          recaptchaToken = await window.grecaptcha.execute("6LeWkNArAAAAAIGOvV76MvVK2LfjEbO_hGb6Ufep", {
            action: "contact_form",
          });
          console.log("reCAPTCHA token received:", recaptchaToken ? "SUCCESS" : "FAILED");
        } catch (error) {
          console.error("reCAPTCHA execution failed:", error);
        }
      } else {
        console.warn("grecaptcha not found on window object");
      }
      console.log("Sending form data...");
      const res = await fetch("/api/sendmail.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          website: "",
          // honeypot field
          recaptcha_token: recaptchaToken,
        }),
      });
      console.log("API response received:", res.status);
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Versturen mislukt");
      }
      toast({
        title: "Bericht verzonden!",
        description: "We nemen binnen 24 uur contact met je op.",
      });
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("Form submission error:", error);
      toast({
        title: "Versturen mislukt",
        description: "Er is iets misgegaan. Probeer het opnieuw of bel ons.",
        variant: "destructive",
      });
    }
  };
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Contact - Compleet Verzekerd</title>
        <meta
          name="description"
          content="Contact Compleet Verzekerd: 078-220 00 21. Van Eesterenplein 95 Dordrecht. Ma-Vr 9:00-17:30. Gratis parkeren. Afspraak maken of direct bellen!"
        />
        <meta
          name="keywords"
          content="contact compleet verzekerd, telefoonnummer dordrecht, kantoor adres, openingstijden, afspraak maken, contactformulier, bereikbaarheid"
        />
        <link rel="canonical" href="https://compleetverzekerd.nl/contact/" />
        <script type="application/ld+json">
          {`{
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "mainEntity": {
              "@type": "LocalBusiness",
              "name": "Compleet Verzekerd",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Van Eesterenplein 95",
                "addressLocality": "Dordrecht",
                "postalCode": "3315 KT",
                "addressCountry": "NL"
              },
              "telephone": "+31782200021",
              "email": "info@compleetverzekerd.nl",
              "openingHours": "Mo-Fr 08:30-17:30"
            }
          }`}
        </script>
        <script
          src="https://www.google.com/recaptcha/api.js?render=6LeWkNArAAAAAIGOvV76MvVK2LfjEbO_hGb6Ufep"
          async
          defer
        ></script>
      </Helmet>
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-hero text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold mb-6">Contact Compleet Verzekerd</h1>
            <p className="text-xl mb-8">
              Heb je vragen of wil je een afspraak maken? We staan voor je klaar en helpen je graag verder.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Details */}
            <div>
              <h2 className="text-3xl font-bold text-secondary mb-8">Neem contact op</h2>

              <div className="space-y-6 mb-8">
                <Card className="p-6">
                  <div className="flex items-start space-x-4">
                    <Phone className="w-6 h-6 text-primary mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Telefoon</h3>
                      <a
                        href="tel:0782200021"
                        className="text-muted-foreground mb-2 hover:text-primary transition-colors block"
                      >
                        078 – 220 00 21
                      </a>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <div className="flex items-start space-x-4">
                    <Mail className="w-6 h-6 text-primary mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-2">E-mail</h3>
                      <p className="text-muted-foreground mb-2">info@compleetverzekerd.nl</p>
                      <p className="text-sm text-muted-foreground">We reageren op werkdagen binnen 24 uur</p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <div className="flex items-start space-x-4">
                    <MapPin className="w-6 h-6 text-primary mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Adres</h3>
                      <p className="text-muted-foreground mb-2">
                        Van Eesterenplein 95
                        <br />
                        3315 KT&nbsp;&nbsp;Dordrecht
                      </p>
                      <p className="text-sm text-muted-foreground">Gratis parkeren beschikbaar</p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <div className="flex items-start space-x-4">
                    <Clock className="w-6 h-6 text-primary mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Openingstijden</h3>
                      <div className="text-muted-foreground space-y-1">
                        <div className="flex justify-between">
                          <span>Maandag - Vrijdag:</span>
                          <span>  9:00 - 17:30</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Emergency Contact */}
            </div>

            {/* Contact Form */}
            <div id="bericht">
              <h3 className="text-2xl font-bold text-secondary mb-8">Stuur ons een bericht</h3>
              <Card className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Naam *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            name: e.target.value,
                          })
                        }
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">Telefoon</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            phone: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">E-mail *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          email: e.target.value,
                        })
                      }
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="subject">Onderwerp *</Label>
                    <Input
                      id="subject"
                      value={formData.subject}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          subject: e.target.value,
                        })
                      }
                      placeholder="Waar gaat je vraag over?"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Bericht *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          message: e.target.value,
                        })
                      }
                      placeholder="Vertel ons waar je hulp bij nodig hebt..."
                      rows={6}
                      required
                    />
                  </div>

                  <Button type="submit" className="w-full bg-primary hover:bg-primary-dark" size="lg">
                    <Send className="w-5 h-5" />
                    Bericht Versturen
                  </Button>

                  <p className="text-sm text-muted-foreground text-center">
                    * Verplichte velden. We reageren op werkdagen binnen 24 uur op je bericht.
                  </p>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-secondary mb-4">Bezoek ons kantoor</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Kom gerust langs voor een persoonlijk gesprek. Ons kantoor is gemakkelijk bereikbaar en er is gratis
              parkeren beschikbaar.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="bg-card rounded-2xl overflow-hidden shadow-lg border">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2467.137098435018!2d4.7179614!3d51.8036555!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c429009dea1e91%3A0xfb46785563a02116!2sVan%20Eesterenplein%2095%2C%203315%20KT%20Dordrecht!5e0!3m2!1snl!2snl!4v1680080075201!5m2!1snl!2snl"
                width="100%"
                height="500"
                style={{
                  border: 0,
                }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full"
                title="Locatie Compleet Verzekerd"
              />
            </div>

            {/* Location Details */}
            <div className="mt-8 grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <MapPin className="w-8 h-8 text-primary mx-auto mb-3" />
                <h3 className="font-semibold text-lg mb-2">Adres</h3>
                <p className="text-muted-foreground">
                  Van Eesterenplein 95
                  <br />
                  3315 KT&nbsp;&nbsp;Dordrecht
                </p>
              </div>

              <div className="text-center">
                <Phone className="w-8 h-8 text-primary mx-auto mb-3" />
                <h3 className="font-semibold text-lg mb-2">Telefoonnummer</h3>
                <a href="tel:0782200021" className="text-primary hover:text-primary-dark transition-colors font-medium">
                  078 – 220 00 21
                </a>
              </div>

              <div className="text-center">
                <Clock className="w-8 h-8 text-primary mx-auto mb-3" />
                <h3 className="font-semibold text-lg mb-2">Bereikbaarheid</h3>
                <p className="text-muted-foreground">Ma-Vr: 8:30 - 17:30</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Appointment Booking Section */}
      <AppointmentBooking />

      <Footer />
      <WhatsAppButton />
    </div>
  );
};
export default Contact;
